import { GatewayAPIClient, gatewayAPIClient } from "../../app/api/client";
import {
  normalizeThemePreference,
  THEME_PREFERENCES,
  type ThemePreference,
} from "../theme/theme";

export type SupportedLocale = "ru" | "en";

export const DEFAULT_SUPPORTED_LOCALE: SupportedLocale = "ru";
export const SUPPORTED_LOCALES: readonly SupportedLocale[] = ["ru", "en"];

export interface SelfProfile {
  display_name: string;
  nickname: string;
  username: string;
  status_text: string;
  avatar_url: string;
  locale?: SupportedLocale;
  theme?: ThemePreference;
}

export interface PatchSelfProfileInput {
  display_name?: string;
  nickname?: string;
  status_text?: string;
  avatar_url?: string;
  locale?: string;
  theme?: ThemePreference;
}

type ProfileAPIClient = Pick<GatewayAPIClient, "request">;

function getClient(client?: ProfileAPIClient): ProfileAPIClient {
  return client ?? gatewayAPIClient;
}

function stringOrEmpty(value: unknown): string {
  return typeof value === "string" ? value : "";
}

export function normalizeSupportedLocale(value: unknown): SupportedLocale {
  if (typeof value !== "string") {
    return DEFAULT_SUPPORTED_LOCALE;
  }

  const normalized = value.trim().toLowerCase();
  if (normalized === "ru" || normalized === "en") {
    return normalized;
  }
  return DEFAULT_SUPPORTED_LOCALE;
}

function isSupportedLocale(value: unknown): value is SupportedLocale {
  return normalizeSupportedLocale(value) === value;
}

function readSupportedThemePreference(value: unknown): ThemePreference | null {
  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  for (const preference of THEME_PREFERENCES) {
    if (normalized === preference) {
      return preference;
    }
  }
  return null;
}

function toSelfProfile(payload: unknown): SelfProfile {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return {
      display_name: "",
      nickname: "",
      username: "",
      status_text: "",
      avatar_url: "",
      locale: DEFAULT_SUPPORTED_LOCALE,
      theme: normalizeThemePreference(undefined),
    };
  }

  const candidate = payload as Record<string, unknown>;
  return {
    display_name: stringOrEmpty(candidate.display_name),
    nickname: stringOrEmpty(candidate.nickname),
    username: stringOrEmpty(candidate.username),
    status_text: stringOrEmpty(candidate.status_text),
    avatar_url: stringOrEmpty(candidate.avatar_url),
    locale: normalizeSupportedLocale(candidate.locale),
    theme: normalizeThemePreference(candidate.theme),
  };
}

function buildPatchBody(input: PatchSelfProfileInput): PatchSelfProfileInput {
  const body: PatchSelfProfileInput = {};

  if (typeof input.display_name === "string") {
    body.display_name = input.display_name;
  }
  if (typeof input.nickname === "string") {
    body.nickname = input.nickname;
  }
  if (typeof input.status_text === "string") {
    body.status_text = input.status_text;
  }
  if (typeof input.avatar_url === "string") {
    body.avatar_url = input.avatar_url;
  }
  if (isSupportedLocale(input.locale)) {
    body.locale = input.locale;
  }
  const supportedTheme = readSupportedThemePreference(input.theme);
  if (supportedTheme !== null) {
    body.theme = supportedTheme;
  }
  if (Object.keys(body).length === 0) {
    throw new Error("profile patch input must include at least one field");
  }

  return body;
}

export async function getSelfProfile(client?: ProfileAPIClient): Promise<SelfProfile> {
  const response = await getClient(client).request<Record<string, unknown>>({
    method: "GET",
    path: "/v1/profile",
  });

  return toSelfProfile(response);
}

export async function patchSelfProfile(
  input: PatchSelfProfileInput,
  client?: ProfileAPIClient,
): Promise<SelfProfile> {
  const response = await getClient(client).request<Record<string, unknown>>({
    method: "PATCH",
    path: "/v1/profile",
    body: buildPatchBody(input),
  });

  return toSelfProfile(response);
}

export async function replaceProfileAvatar(
  avatar: Blob,
  filename = "avatar.png",
  client?: ProfileAPIClient,
): Promise<SelfProfile> {
  const normalizedFilename = filename.trim() === "" ? "avatar.png" : filename.trim();
  const body = new FormData();
  body.append("avatar", avatar, normalizedFilename);

  const response = await getClient(client).request<Record<string, unknown>>({
    method: "PATCH",
    path: "/v1/profile",
    body,
  });

  return toSelfProfile(response);
}

export async function removeProfileAvatar(client?: ProfileAPIClient): Promise<SelfProfile> {
  return patchSelfProfile(
    {
      avatar_url: "",
    },
    client,
  );
}
